import { useEffect, useMemo, useState } from "react";
import { Upload, Download, FileDown, FileUp, Loader2, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { authenticatedDownload } from "./useBulkJob";
import ImportDialog from "./ImportDialog";

/**
 * BulkActionsBar — drop this above any master list.
 * Props:
 *   - masterKey: string (e.g. "users")
 *   - label:     string used for toast/UI (e.g. "Users")
 *   - canImport: bool — hide/disable Import if user lacks perm
 *   - canExport: bool
 *   - buildExportFilters: (returns object of current filters) — passed as `?filters=<json>`
 *   - onImportDone: (job) => void  fired when a completed job is closed
 */
export default function BulkActionsBar({
  masterKey,
  label,
  caption,
  hint,
  canImport = true,
  canExport = true,
  buildExportFilters = () => ({}),
  onImportDone,
}) {
  const [importOpen, setImportOpen] = useState(false);
  const [exporting, setExporting] = useState(false);

  const doExport = async (format) => {
    setExporting(true);
    try {
      const filters = buildExportFilters() || {};
      const filtersQ = Object.keys(filters).length
        ? `&filters=${encodeURIComponent(JSON.stringify(filters))}`
        : "";
      const suggestedName = `${masterKey}_export_${new Date()
        .toISOString().replace(/[:.]/g, "-").slice(0, 16)}.${format}`;
      await authenticatedDownload(
        `/bulk/${masterKey}/export?format=${format}${filtersQ}`,
        suggestedName,
      );
      toast.success(`${label} exported`);
    } catch (e) {
      toast.error(`Export failed: ${e?.message || e}`);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="flex flex-col gap-0.5" data-testid={`bulk-actions-${masterKey}`} title={hint}>
      {caption && <div className="text-[10px] uppercase tracking-widest text-neutral-400 leading-none">{caption}</div>}
      <div className="flex items-center gap-2">
      {canImport && (
        <Button
          variant="outline"
          onClick={() => setImportOpen(true)}
          data-testid={`bulk-import-open-${masterKey}`}
          className="h-10 border-neutral-200"
        >
          <FileUp className="h-4 w-4 mr-2" /> Import
        </Button>
      )}
      {canExport && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              data-testid={`bulk-export-menu-${masterKey}`}
              disabled={exporting}
              className="h-10 border-neutral-200"
            >
              {exporting
                ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                : <FileDown className="h-4 w-4 mr-2" />}
              Export
              <ChevronDown className="h-3.5 w-3.5 ml-2 opacity-60" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-40">
            <DropdownMenuItem
              onClick={() => doExport("xlsx")}
              data-testid={`bulk-export-xlsx-${masterKey}`}
            >
              <Download className="h-4 w-4 mr-2" /> Excel (.xlsx)
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => doExport("csv")}
              data-testid={`bulk-export-csv-${masterKey}`}
            >
              <Download className="h-4 w-4 mr-2" /> CSV
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      {importOpen && (
        <ImportDialog
          masterKey={masterKey}
          label={label}
          open={importOpen}
          onOpenChange={(v) => {
            setImportOpen(v);
            if (!v && onImportDone) onImportDone(null);
          }}
          onDone={onImportDone}
        />
      )}
      </div>
    </div>
  );
}
